'use client'

import cx from 'classnames'
import Image from 'next/image'
import { ReactNode, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import backgroundImage from '@/assets/images/products/bg_set.jpg'
import Picture from '@/components/Image/Picture'
import '@/components/Product/ProductCard/ProductCard.scss'
import { ProductType } from '@/types/product'

export default function ProductCardLogic({
  product,
  children = <></>,
}: {
  product: ProductType
  children?: ReactNode
}) {
  const [hover, setHover] = useState(false)
  const { inView, ref } = useInView({
    threshold: 0,
    rootMargin: '0px 150% -30%',
    triggerOnce: true,
  })

  const [src, width, height] =
    product.productGroup?.creative != null
      ? [
          product.productGroup.creative.publishUrl,
          product.productGroup.creative.width,
          product.productGroup.creative.height,
        ]
      : [backgroundImage.src, backgroundImage.width, backgroundImage.height]

  return (
    <div
      className="c-product-productCard"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      ref={ref}
    >
      <figure
        className={cx('c-product-productCard__bg', {
          'c-product-productCard__bg--hover': hover,
        })}
      >
        <Image src={src} width={width} height={height} alt={product.name} />
      </figure>

      {product.label && (
        <p
          className={cx('c-product-productCard__tag', {
            'c-product-productCard__tag--inview': inView,
          })}
        >
          {product.label}
        </p>
      )}

      <p
        className={cx('u-font-gb c-product-productCard__name', {
          'c-product-productCard__text--hover': hover,
          'c-product-productCard__name--inview': inView,
        })}
      >
        {Array.from(product.name).map((char, index) => {
          return (
            <span className="char" key={`${char}-${index}`}>
              {char}
            </span>
          )
        })}
      </p>
      <p
        className={cx('u-font-yu-min c-product-productCard__ruby', {
          'c-product-productCard__text--hover': hover,
          'c-product-productCard__ruby--inview': inView,
        })}
      >
        {product.i18nName}
      </p>
      <div className="c-product-productCard__content">
        <Picture
          className={cx('c-product-productCard__image', {
            'c-product-productCard__image--inview': inView,
          })}
          src={product.creative.publishUrl}
          width={product.creative.width}
          height={product.creative.height}
          spSrc={product.creative.spPublishUrl}
          spWidth={product.creative.spWidth}
          spHeight={product.creative.spHeight}
          alt={product.name}
        />
      </div>
      <div className="c-product-productCard__price">
        <p
          className={cx({
            'c-product-productCard__text--hover': hover,
          })}
        >
          <span className="c-product-productCard__price--main">
            ¥{product.invoiceItem.listPriceWithTax.toLocaleString()}
          </span>
          <span className="c-product-productCard__price--tax">[税込]</span>
          <span className="c-product-productCard__price--option">/ {product.capacity}</span>
        </p>
      </div>

      {children}
    </div>
  )
}
